import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useHistory, useLocation } from 'react-router-dom'
import { useTheme } from '@mui/material'

import { useTranslation } from 'language/translator'

import { useIsMobile, useParams, usePrevious } from 'utils/hooks'

/* Molecules */
import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'

/* Atoms */
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'

/* Organisms */
import Dashboard from 'components_new/organisms/Dashboard'
import DashboardTabs from 'components_new/organisms/DashboardTabs'
import DashboardUserFilter from 'components_new/organisms/DashboardUserFilter'
import DashboardUserFilterBar from 'components_new/organisms/DashboardUserFilterBar'
import AboutDashboardDialog, {
  TVariant as TAboutVariant
} from 'components_new/organisms/dialogs/AboutDashboardDialog'
import ActivityDialog from 'components_new/organisms/dialogs/ActivityDialog'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import EditTitleDialog from 'components_new/organisms/dialogs/EditTitleDialog'
import ExportDialog from 'components_new/organisms/dialogs/ExportDialog'
import MoveDialog from 'components_new/organisms/dialogs/MoveDialog'
import NewDashboardFromExampleDialog from 'components_new/organisms/dialogs/NewDashboardFromExampleDialog'
import ShareDashboardDialog from 'components_new/organisms/dialogs/ShareDashboardDialog'

import { addRef } from 'utils/queryHandler'
import { getContext } from 'utils/userEvents'

/* Templates */
import PageLayout from 'components_new/templates/PageLayout'

import { useDashboardOptions } from './utils'
import TopbarActions from './topbar/Actions'
import TopbarContent from './topbar/Content'

/* Actions */
import { useDashboardActions } from 'redux/actions/Dashboards'
import { useDashboardGroupActions } from 'redux/actions/DashboardGroups'
import { useResourceStatusActions } from 'redux/actions/ResourceStatuses'
import { useDashboardPageStateActions } from 'redux/actions/DashboardPage'
import { useWidgetActions } from 'redux/actions/Widgets'
import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'
import * as UserEvents from 'redux/api/internal/UserEvents'

/* Hooks */
import { useCustomization } from 'redux/hooks/Customization'
import { useDashboard, useDashboardFetchStatus } from 'redux/hooks/Dashboards'
import { useDashboardGroup } from 'redux/hooks/DashboardGroups'
import {
  useDashboardFilter,
  useEditMode,
  usePeriodFilter
} from 'redux/hooks/DashboardPage'
import { useAllowEditDashboardGroup } from 'redux/hooks/Permissions'

/* Types */
import { DashboardVariantType } from 'types/GlobalDashboard'
import { DashboardStatus } from 'types/GlobalDashboardGroup'
import { TDashboardLocationState } from 'types/GlobalDashboardFilter'
import { Action, Context } from 'types/GlobalUserEvents'
import { TCoreWidget } from 'types/GlobalWidget'
import { useAppReady } from 'redux/hooks/AppInit'
import { useSetDashboardFilter } from 'helpers/hooks/Dashboards'
import { TBreadcrumbItem } from 'components_new/molecules/Breadcrumbs'

export type TDialogType =
  | 'dashboard_activity'
  | 'delete_dashboard'
  | 'edit_dashboard_title'
  | 'edit_template'
  | 'export_dashboard'
  | 'move_group'
  | 'share_dashboard'

const DashboardPage = () => {
  const params = useParams<{ id: string }>()
  const { id } = useParams()
  const theme = useTheme()
  const history = useHistory()
  const appReady = useAppReady()

  const prevParams = usePrevious(params)
  const dashboard = useDashboard(params.id)
  const dashboardFetchStatus = useDashboardFetchStatus(params.id)
  const dashboardGroup = useDashboardGroup(
    dashboard?.dashboard_group_id || null
  )
  const allowEditDashboardGroup = useAllowEditDashboardGroup(
    dashboard?.dashboard_group_id
  )
  const isMobile = useIsMobile()
  const editMode = useEditMode()
  const { updateOneDashboardPageEntry, updateManyDashboardPageEntires } =
    useDashboardPageStateActions()

  const handleToggleEditMode = useCallback(() => {
    const searchParams = new URLSearchParams(search)
    const newlyCreated = searchParams.get('new_dashboard')

    if (Boolean(newlyCreated)) {
      history.replace(`/dashboards/${id}`)
    }

    updateOneDashboardPageEntry({ key: 'editMode', value: !editMode })
  }, [editMode])

  const customization = useCustomization()
  const hasLoggedOpenedDashboard = useRef(false)
  const dashboardFilter = useDashboardFilter()
  const periodFilter = usePeriodFilter()
  const { tryGetOneDashboardWidget } = useDashboardWidgetActions()
  const { invalidateWidgets } = useWidgetActions()

  const location = useLocation<TDashboardLocationState | undefined>()
  const { search } = location
  const { t } = useTranslation()
  const setDashboardFilter = useSetDashboardFilter()

  const searchParams = new URLSearchParams(search)
  const newlyCreated = searchParams.get('new_dashboard')

  const {
    tryCopyDashboardGroup,
    tryDeleteDashboardGroup,
    tryUpdateDashboardGroup
  } = useDashboardGroupActions()

  const { tryPutResourceStatus } = useResourceStatusActions()

  const breadcrumbs: TBreadcrumbItem[] = [
    {
      href: '/resources',
      icon: { name: 'Resources' },
      title: t('global', 'resources')
    },
    ...(dashboardGroup
      ? [
          {
            href: `/dashboards/${dashboardGroup.firstDashboardId}`,
            icon: { name: 'Dashboards' as const },
            title: dashboardGroup.title
          }
        ]
      : [])
  ]

  const { tryGetOneDashboard } = useDashboardActions()
  const [dialog, setDialog] = useState<{
    type: TDialogType
    open: boolean
    id: string
  }>({
    type: 'delete_dashboard',
    open: false,
    id: ''
  })
  const [aboutDialog, setAboutDialog] = useState<{
    variant: TAboutVariant
    open: boolean
  }>({
    variant: 'info',
    open: false
  })

  const handleOpenDialog = (type: TDialogType, id?: string) => {
    setDialog({ type, open: true, id: id ?? '' })
  }

  // Pre-configured fetch function for individual widgets
  const fetchWidgetData = useCallback(
    (widget: TCoreWidget) => {
      tryGetOneDashboardWidget(widget, id)
    },
    [id]
  )

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const dashboardOptions = useDashboardOptions({
    onEditLayout: () => handleToggleEditMode(),
    onEditTitle: () => handleOpenDialog('edit_dashboard_title', id),
    onExport: () => handleOpenDialog('export_dashboard'),
    onDelete: () => handleOpenDialog('delete_dashboard', id),
    onDuplicate: () => {
      if (!dashboardGroup) return

      tryCopyDashboardGroup(
        dashboardGroup.id,
        (id) => history.push(addRef(`/dashboards/${id}`, Context.DUPLICATE)),
        true
      )
    },
    onMove: () => handleOpenDialog('move_group', id),
    onPublish: () => {
      if (!dashboardGroup) return

      if (dashboardGroup.status === DashboardStatus.PUBLISHED) {
        tryUpdateDashboardGroup(
          dashboardGroup.id,
          { status: DashboardStatus.DRAFT },
          (e) => {
            if (!e) {
              UserEvents.log({
                action: Action.DASHBOARD_UNPUBLISHED,
                context: null,
                dashboard_id: null,
                dashboard_group_id: dashboardGroup.id
              })
            }
          }
        )
      } else {
        setAboutDialog({ open: true, variant: 'publish' })
      }
    },
    onShare: () => handleOpenDialog('share_dashboard'),
    onShowActivity: () => handleOpenDialog('dashboard_activity')
  })

  /**
   * If dashboard_group changes, reset dashboard specific settings.
   */
  useEffect(() => {
    if (dashboard?.dashboard_group_id) {
      updateManyDashboardPageEntires([
        { key: 'dashboardFilter', value: [] },
        { key: 'periodFilter', value: null },
        { key: 'savedDashboardFilter', value: null }
      ])

      const linkedFilter = location.state?.dashboardFilter

      if (linkedFilter && linkedFilter.length > 0) {
        setDashboardFilter(linkedFilter)

        // clear the state so the filter is applied only once
        history.replace({ ...location, state: undefined })
      }

      if (Boolean(newlyCreated)) {
        updateOneDashboardPageEntry({ key: 'editMode', value: true })
      } else {
        updateOneDashboardPageEntry({ key: 'editMode', value: false })
      }
    }
  }, [dashboard?.dashboard_group_id])

  useEffect(() => {
    // Always fetch dashboard when params.id changes
    // This resets widget data and settings
    if (appReady) {
      tryGetOneDashboard(params.id)
    }

    // if dashboard has changed then allow log.
    if (
      hasLoggedOpenedDashboard.current &&
      params.id &&
      prevParams?.id &&
      params.id !== prevParams.id
    ) {
      hasLoggedOpenedDashboard.current = false
    }

    if (params.id && !hasLoggedOpenedDashboard.current) {
      const context = getContext(searchParams)

      UserEvents.log({
        action: Action.DASHBOARD_OPENED,
        context: context,
        dashboard_id: params.id,
        dashboard_group_id: dashboardGroup?.id
      })

      // Make sure we only log one open dashboard.
      hasLoggedOpenedDashboard.current = true
    }
  }, [params.id, appReady])

  // Invalidate widgets when dashboard changes
  useEffect(() => {
    if (dashboard?.id) {
      invalidateWidgets()
    }
  }, [dashboard?.id])

  if (dashboardFetchStatus.status === 'error') {
    const errorCode = dashboardFetchStatus.errorStatusCode

    const actions = (
      <Button
        onClick={() => history.push('/dashboards')}
        startIcon={<Icon name="ArrowBackOutlined" />}
        variant="text"
      >
        {t('global', 'dashboards')}
      </Button>
    )

    return (
      <PageLayout
        topbar={{
          breadcrumbs: breadcrumbs,
          mobile: { goBack: true }
        }}
        variant="static"
      >
        {errorCode === 401 || errorCode === 403 ? (
          <ErrorPlaceholder
            code={401}
            body={t('dashboards', 'error_no_access_body')}
            actions={actions}
          />
        ) : errorCode === 404 ? (
          <ErrorPlaceholder
            code={404}
            body={t('dashboards', 'error_not_found_body')}
            actions={actions}
          />
        ) : (
          <ErrorPlaceholder
            code={500}
            body={t('dashboards', 'error_server_body')}
            actions={
              <>
                <Button
                  onClick={() => tryGetOneDashboard(params.id)}
                  startIcon={<Icon name="RefreshOutlined" />}
                >
                  {t('global', 'try_again')}
                </Button>
                {actions}
              </>
            }
          />
        )}
      </PageLayout>
    )
  }

  return (
    <>
      <Helmet>
        <meta name="themecolor" content={theme.palette.background.default} />
      </Helmet>
      <PageLayout
        content={{ disablePadding: true }}
        footer={{
          content: !isMobile ? (
            <DashboardTabs
              dashboardGroup={dashboardGroup}
              dashboard={dashboard}
            />
          ) : undefined,
          disablePadding: true
        }}
        rightSidebar={{
          content: (
            <DashboardUserFilter
              dashboard={dashboard}
              dashboardGroup={dashboardGroup}
              dashboardFilters={dashboardGroup?.dashboard_filters || []}
            />
          ),
          mobile: {
            preview: <DashboardUserFilterBar dashboard={dashboard} />,
            title: t('filters', 'filters')
          },
          size: 'small'
        }}
        toolbar={{
          collapsed: dashboardFilter.length === 0 && !Boolean(periodFilter),
          content: <DashboardUserFilterBar dashboard={dashboard} />
        }}
        topbar={{
          actions: (
            <TopbarActions
              dashboardGroup={dashboardGroup}
              onOpenDialog={handleOpenDialog}
              onToggleEditMode={handleToggleEditMode}
            />
          ),
          breadcrumbs: breadcrumbs,
          content: (
            <TopbarContent
              dashboardGroup={dashboardGroup}
              onOpenAboutDialog={() =>
                setAboutDialog({ variant: 'info', open: true })
              }
              onToggleFavorite={() => {
                if (dashboardGroup) {
                  tryPutResourceStatus(
                    dashboardGroup.id,
                    'dashboard',
                    dashboardGroup.favorite
                  )
                }
              }}
            />
          ),
          mobile: {
            goBack: true
          },
          options: allowEditDashboardGroup ? dashboardOptions : undefined
        }}
        variant="static"
      >
        <Dashboard
          customization={customization}
          dashboard={dashboard}
          dashboardFilters={dashboardGroup?.dashboard_filters || []}
          fetchWidgetData={fetchWidgetData}
          variant={{
            embedded: false,
            type: DashboardVariantType.PORTAL
          }}
        />
        <NewDashboardFromExampleDialog />
        <ExportDialog
          dashboard={dashboard}
          open={dialog.open && dialog.type === 'export_dashboard'}
          onClose={handleCloseDialog}
          onOpen={() => handleOpenDialog('export_dashboard')}
        />
      </PageLayout>

      {/* dialogs */}
      <ActivityDialog
        dashboardGroup={dashboardGroup}
        open={dialog.open && dialog.type === 'dashboard_activity'}
        onClose={handleCloseDialog}
      />

      <DeleteDialog
        onClose={handleCloseDialog}
        onDelete={
          !dashboardGroup
            ? () => {}
            : (callback: () => void) => {
                tryDeleteDashboardGroup(
                  dashboardGroup.id,
                  dashboardGroup.status,
                  () => {
                    history.push('/dashboards/')
                    handleCloseDialog()
                    callback()
                  }
                )
              }
        }
        open={dialog.open && dialog.type === 'delete_dashboard'}
        title={dashboardGroup?.title}
        type="dashboard"
      />
      <EditTitleDialog
        onClose={handleCloseDialog}
        onSubmit={(value) => {
          if (dashboardGroup) {
            tryUpdateDashboardGroup(dashboardGroup.id, { title: value })
          }
        }}
        open={dialog.open && dialog.type === 'edit_dashboard_title'}
        title={dashboardGroup?.title}
      />
      <MoveDialog
        folderId={dashboardGroup?.folder_id ?? null}
        onClose={handleCloseDialog}
        onSubmit={(folderId) => {
          if (dashboardGroup) {
            tryUpdateDashboardGroup(dashboardGroup.id, {
              folder_id: folderId
            })
          }
        }}
        open={dialog.open && dialog.type === 'move_group'}
      />
      <AboutDashboardDialog
        dashboardGroup={dashboardGroup}
        onClose={() =>
          setAboutDialog((prevState) => ({ ...prevState, open: false }))
        }
        open={aboutDialog.open}
        variant={aboutDialog.variant}
        setVariant={(variant: TAboutVariant) =>
          setAboutDialog((prev) => ({ ...prev, variant }))
        }
      />
      <ShareDashboardDialog
        dashboard={dashboard}
        dashboardGroup={dashboardGroup}
        onClose={handleCloseDialog}
        onOpen={() => handleOpenDialog('share_dashboard')}
        open={dialog.open && dialog.type === 'share_dashboard'}
      />
    </>
  )
}

export default DashboardPage
